import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { bibleBooks } from '../lib/bibleReference';

// bibleBooks is in canonical order: the first 39 are the Old Testament.
const BOOKS = Object.values(bibleBooks);
const OLD_COUNT = 39;

/**
 * Pick a passage by clicking: book, then chapter, then (optionally) verses.
 * Choosing a chapter calls `onSelect("John 3")` straight away (it only stages
 * it, nothing goes on screen); the verse boxes narrow it ("John 3:16-18").
 * Prev/Next chapter buttons let you page through a book.
 */
const BiblePicker = ({ onSelect, dm, t }) => {
  const [book, setBook] = useState(null);       // one of BOOKS
  const [chapter, setChapter] = useState(null);
  const [filter, setFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return BOOKS.map((b, i) => ({ ...b, testament: i < OLD_COUNT ? 'old' : 'new' }))
      .filter(b => !q || b.name.toLowerCase().includes(q) || b.abbr.some(a => a.startsWith(q)));
  }, [filter]);

  const chip = `px-2.5 py-1.5 rounded-lg text-sm font-medium border transition-all ${dm
    ? 'bg-slate-800 border-slate-700 text-slate-100 hover:bg-slate-700 hover:border-blue-400'
    : 'bg-white border-gray-300 text-gray-800 hover:bg-blue-50 hover:border-blue-400'}`;
  const chipOn = 'bg-blue-600 border-blue-500 text-white';
  const label = `block text-xs font-semibold mb-1 ${t.textSecondary}`;

  const pickBook = (b) => { setBook(b); setChapter(null); setFrom(''); setTo(''); setFilter(''); };
  const pickChapter = (n) => {
    setChapter(n); setFrom(''); setTo('');
    onSelect(`${book.name} ${n}`);
  };
  const showVerses = () => {
    const a = parseInt(from, 10);
    const b = parseInt(to, 10);
    if (!(a > 0)) { onSelect(`${book.name} ${chapter}`); return; }
    onSelect(b > a ? `${book.name} ${chapter}:${a}-${b}` : `${book.name} ${chapter}:${a}`);
  };

  // ---- step 1: books ----
  if (!book) {
    const groups = [['Old Testament', visible.filter(b => b.testament === 'old')], ['New Testament', visible.filter(b => b.testament === 'new')]];
    return (
      <div>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter books…"
          aria-label="Filter books"
          className={`w-full p-2.5 mb-3 text-sm rounded-lg border outline-none focus:border-brand-500 ${t.inputBg}`}
        />
        {visible.length === 0 && <p className={`text-sm ${t.textSecondary}`}>No book matches “{filter}”.</p>}
        {groups.map(([title, list]) => list.length > 0 && (
          <div key={title} className="mb-3">
            <div className={label}>{title}</div>
            <div className="flex flex-wrap gap-1.5">
              {list.map(b => (
                <button key={b.name} type="button" className={chip} onClick={() => pickBook(b)}>{b.name}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ---- step 2/3: chapters, then verses ----
  const chapters = Array.from({ length: book.chapters }, (_, i) => i + 1);
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3">
        <button type="button" className={`${chip} flex items-center gap-1`} onClick={() => { setBook(null); setChapter(null); }}>
          <ChevronLeft className="w-4 h-4" /> All books
        </button>
        <div className={`text-base font-bold truncate ${t.textPrimary}`}>
          {book.name}{chapter ? ` ${chapter}` : ''}
        </div>
        {chapter ? (
          <div className="flex gap-1">
            <button type="button" className={chip} disabled={chapter <= 1} aria-label="Previous chapter" title="Previous chapter"
              onClick={() => pickChapter(chapter - 1)}><ChevronLeft className="w-4 h-4" /></button>
            <button type="button" className={chip} disabled={chapter >= book.chapters} aria-label="Next chapter" title="Next chapter"
              onClick={() => pickChapter(chapter + 1)}><ChevronRight className="w-4 h-4" /></button>
          </div>
        ) : <span />}
      </div>

      <div className={label}>{chapter ? 'Chapter' : 'Choose a chapter'}</div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {chapters.map(n => (
          <button
            key={n}
            type="button"
            aria-label={`Chapter ${n}`}
            aria-pressed={chapter === n}
            className={`${chip} min-w-[2.5rem] ${chapter === n ? chipOn : ''}`}
            onClick={() => pickChapter(n)}
          >
            {n}
          </button>
        ))}
      </div>

      {chapter && (
        <div className={`pt-3 border-t ${t.divider || ''}`}>
          <div className={label}>Only some verses? (leave empty for the whole chapter)</div>
          <div className="flex items-center gap-2">
            <input type="number" min="1" inputMode="numeric" value={from} onChange={(e) => setFrom(e.target.value)}
              placeholder="From" aria-label="From verse" className={`w-24 p-2 text-sm rounded-lg border outline-none ${t.inputBg}`} />
            <span className={t.textSecondary}>to</span>
            <input type="number" min="1" inputMode="numeric" value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="To" aria-label="To verse" className={`w-24 p-2 text-sm rounded-lg border outline-none ${t.inputBg}`} />
            <button type="button" onClick={showVerses}
              className="px-3 py-2 rounded-lg text-sm font-semibold bg-brand-600 text-slate-50 hover:bg-brand-500">
              Stage verses
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BiblePicker;
